import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Play, Eye, CheckCheck, Users, ChevronDown, ChevronUp, Sparkles, Loader2, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../services/api";
import { Button } from "../components/ui/button";

const REVIEW_STATUS_STYLES = {
  pending:     { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  in_progress: { color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
  completed:   { color: "#22c55e", bg: "rgba(34,197,94,0.12)" },
  approved:    { color: "#8b5cf6", bg: "rgba(139,92,246,0.12)" },
};

export default function PerformanceReviews() {
  const { t } = useTranslation();
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLaunch, setShowLaunch] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [expandedReview, setExpandedReview] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [aiSummaries, setAiSummaries] = useState({});
  const [aiLoading, setAiLoading] = useState({});

  useEffect(() => {
    api.get("v1/performance/cycles/")
      .then((r) => {
        const list = r.data || [];
        setCycles(list);
        const active = list.find((c) => c.status === "active");
        if (active) setSelectedCycleId(String(active.id));
      })
      .catch(() => {});
    api.get("v1/employees/")
      .then((r) => setEmployees(r.data?.employees || r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCycleId) return;
    loadReviews();
  }, [selectedCycleId]);

  const loadReviews = () => {
    if (!selectedCycleId) return;
    setLoading(true);
    api.get(`v1/performance/cycles/${selectedCycleId}/reviews/`)
      .then((r) => setReviews(r.data || []))
      .catch(() => toast.error(t("performance.reviews.toasts.loadError")))
      .finally(() => setLoading(false));
  };

  const toggleEmployee = (id) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleLaunch = async () => {
    if (!selectedCycleId || selectedEmployees.length === 0) {
      toast.error(t("performance.reviews.requiredSelection", { defaultValue: "Select a cycle and at least one employee" }));
      return;
    }
    setLaunching(true);
    try {
      await api.post(`v1/performance/cycles/${selectedCycleId}/reviews/launch`, {
        employee_ids: selectedEmployees,
      });
      toast.success(t("performance.reviews.toasts.launched", { count: selectedEmployees.length }));
      setShowLaunch(false);
      setSelectedEmployees([]);
      loadReviews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("performance.reviews.toasts.launchError"));
    } finally {
      setLaunching(false);
    }
  };

  const handleScore = async (reviewId) => {
    const input = window.prompt(t("performance.reviews.scorePrompt", "Note finale sur 5 (ex: 4.2) :"));
    if (input === null) return;
    const score = parseFloat(String(input).replace(",", "."));
    if (isNaN(score) || score < 0 || score > 5) {
      toast.error(t("performance.reviews.scoreInvalid", "Entrez une note valide entre 0 et 5"));
      return;
    }
    try {
      await api.patch(`v1/performance/reviews/${reviewId}/score`, { final_score: score });
      toast.success(t("performance.reviews.toasts.scored", "Évaluation notée"));
      loadReviews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      const r = await api.patch(`v1/performance/reviews/${reviewId}/approve`);
      const raise = r.data?.raise_applied;
      if (raise) {
        toast.success(t("performance.reviews.toasts.approvedRaise", {
          sign: raise.delta_pct > 0 ? "+" : "",
          pct: raise.delta_pct,
          prev: Number(raise.previous_salary).toLocaleString(),
          next: Number(raise.new_salary).toLocaleString(),
        }));
      } else if (r.data?.raise_skipped_reason) {
        toast.warning(t("performance.reviews.toasts.raiseSkipped", { reason: r.data.raise_skipped_reason }));
      } else {
        toast.success(t("performance.reviews.toasts.approved"));
      }
      loadReviews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const loadSummary = async (reviewId) => {
    if (summaries[reviewId]) return;
    try {
      const r = await api.get(`v1/performance/reviews/${reviewId}/summary`);
      setSummaries((prev) => ({ ...prev, [reviewId]: r.data }));
    } catch (e) {
      // summary may not exist yet if not completed
    }
  };

  const handleToggle = (reviewId) => {
    const next = expandedReview === reviewId ? null : reviewId;
    setExpandedReview(next);
    if (next) loadSummary(next);
  };

  const generateAiSummary = async (reviewId) => {
    setAiLoading((s) => ({ ...s, [reviewId]: true }));
    try {
      const r = await api.post(`v1/performance/reviews/${reviewId}/ai-summary`);
      setAiSummaries((s) => ({ ...s, [reviewId]: r.data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("performance.reviews.toasts.aiError"));
    } finally {
      setAiLoading((s) => ({ ...s, [reviewId]: false }));
    }
  };

  const alreadyLaunched = new Set(reviews.map((r) => r.reviewee_id));
  const notLaunched = employees.filter((e) => !alreadyLaunched.has(e.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold" style={{ color: "var(--lm-ink)" }}>{t("performance.reviews.title")}</h3>
        <div className="flex items-center gap-2">
          <select
            className="rounded-md border px-3 py-1.5 text-sm"
            style={{ backgroundColor: "var(--lm-canvas)", borderColor: "var(--lm-border)", color: "var(--lm-ink)" }}
            value={selectedCycleId}
            onChange={(e) => setSelectedCycleId(e.target.value)}
          >
            <option value="">{t("performance.reviews.selectCycle")}</option>
            {cycles.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
          {selectedCycleId && (
            <Button size="sm" onClick={() => setShowLaunch(!showLaunch)}>
              <Play className="h-4 w-4 mr-1.5" />
              {t("performance.reviews.launch")}
            </Button>
          )}
        </div>
      </div>

      {/* Launch panel */}
      {showLaunch && (
        <div className="rounded-xl p-5 space-y-3" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #0ea5e9" }}>
          <h4 className="font-semibold text-sm" style={{ color: "var(--lm-ink)" }}>
            {t("performance.reviews.launchTitle")}
          </h4>
          {notLaunched.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
              {t("performance.reviews.allHaveReview")}
            </p>
          ) : (
            <>
              <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
                {t("performance.reviews.selectPrompt", { count: selectedEmployees.length })}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {notLaunched.map((emp) => (
                  <label
                    key={emp.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer"
                    style={{
                      backgroundColor: selectedEmployees.includes(emp.id)
                        ? "rgba(14,165,233,0.08)"
                        : "var(--lm-canvas)",
                      border: `1px solid ${selectedEmployees.includes(emp.id) ? "#0ea5e9" : "var(--lm-border)"}`,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(emp.id)}
                      onChange={() => toggleEmployee(emp.id)}
                      className="accent-sky-500"
                    />
                    <span className="text-sm" style={{ color: "var(--lm-ink)" }}>
                      {emp.first_name} {emp.last_name}
                    </span>
                    {emp.department_name && (
                      <span className="text-xs ml-auto" style={{ color: "var(--lm-ink-3)" }}>{emp.department_name}</span>
                    )}
                  </label>
                ))}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setShowLaunch(false)}>{t("common.cancel")}</Button>
                <Button size="sm" onClick={handleLaunch} disabled={launching || selectedEmployees.length === 0}>
                  {launching ? t("performance.reviews.launching") : t("performance.reviews.launchCta", { count: selectedEmployees.length })}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Reviews list */}
      {!selectedCycleId ? (
        <div className="rounded-xl p-8 text-center" style={{ backgroundColor: "var(--lm-s2)", border: "1px dashed var(--lm-border)" }}>
          <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{t("performance.reviews.selectCyclePrompt")}</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin h-6 w-6 rounded-full border-b-2" style={{ borderColor: "#0ea5e9" }} />
        </div>
      ) : reviews.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ backgroundColor: "var(--lm-s2)", border: "1px dashed var(--lm-border)" }}>
          <Users className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--lm-ink-3)" }} />
          <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
            {t("performance.reviews.emptyPre")}<strong>{t("performance.reviews.launch")}</strong>{t("performance.reviews.emptyPost")}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              t={t}
              summary={summaries[review.id]}
              aiSummary={aiSummaries[review.id]}
              aiLoading={aiLoading[review.id]}
              expanded={expandedReview === review.id}
              onToggle={() => handleToggle(review.id)}
              onApprove={() => handleApprove(review.id)}
              onScore={() => handleScore(review.id)}
              onAiSummarize={() => generateAiSummary(review.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewCard({ review, t, summary, aiSummary, aiLoading, expanded, onToggle, onApprove, onScore, onAiSummarize }) {
  const sty = REVIEW_STATUS_STYLES[review.status] || REVIEW_STATUS_STYLES.pending;
  const statusLabel = t(`performance.reviews.status.${review.status}`, { defaultValue: t("performance.reviews.status.pending") });
  const progress = review.total_feedbacks > 0
    ? Math.round((review.submitted_feedbacks / review.total_feedbacks) * 100)
    : 0;

  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}>
      <div className="flex items-center justify-between px-4 py-3.5">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="min-w-0">
            <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>
              {review.reviewee_name || t("performance.reviews.employeeFallback", { id: review.reviewee_id })}
            </p>
            <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
              {t("performance.reviews.feedbacksSubmitted", { submitted: review.submitted_feedbacks, total: review.total_feedbacks })}
              {review.final_score != null && t("performance.reviews.scoreSuffix", { score: review.final_score.toFixed(2) })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-3">
          <span className="text-xs hidden sm:block" style={{ color: "var(--lm-ink-2)" }}>{progress}%</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: sty.bg, color: sty.color }}>
            {statusLabel}
          </span>
          {review.status !== "completed" && review.status !== "approved" && (
            <button
              onClick={onScore}
              className="px-2 py-1 rounded-md text-xs font-medium hover:opacity-80 transition-opacity"
              title={t("performance.reviews.scoreTitle", "Saisir le score (RH)")}
              style={{ backgroundColor: "rgba(16,185,129,0.12)", color: "#10b981" }}
            >
              {t("performance.reviews.scoreCta", "Noter")}
            </button>
          )}
          {review.status === "completed" && (
            <button
              onClick={onApprove}
              className="p-1.5 rounded-md hover:opacity-80 transition-opacity"
              title={t("performance.reviews.approveTitle")}
              style={{ backgroundColor: "rgba(139,92,246,0.12)", color: "#8b5cf6" }}
            >
              <CheckCheck className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            onClick={onToggle}
            className="p-1.5 rounded-md"
            style={{ color: "var(--lm-ink-3)" }}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-2">
        <div className="h-1.5 rounded-full" style={{ backgroundColor: "var(--lm-border)" }}>
          <div
            className="h-1.5 rounded-full transition-all"
            style={{ width: `${progress}%`, backgroundColor: "#0ea5e9" }}
          />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-2 space-y-3" style={{ borderTop: "1px solid var(--lm-border)" }}>
          {review.salary_recommendation_pct != null && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)" }}
            >
              <span style={{ color: "#22c55e" }}>{t("performance.reviews.salaryRecommendation")}</span>
              <span className="font-semibold" style={{ color: "#22c55e" }}>
                +{review.salary_recommendation_pct}%
              </span>
            </div>
          )}

          {summary ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold" style={{ color: "var(--lm-ink-2)" }}>{t("performance.reviews.scoresByRole")}</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(summary.role_averages || {}).map(([role, score]) => (
                  <div key={role} className="flex items-center justify-between px-3 py-2 rounded-lg text-xs"
                    style={{ backgroundColor: "var(--lm-canvas)", border: "1px solid var(--lm-border)" }}>
                    <span style={{ color: "var(--lm-ink-2)" }}>{t(`performance.reviews.roleLabels.${role}`, { defaultValue: role })}</span>
                    <span className="font-semibold" style={{ color: "var(--lm-ink)" }}>
                      {Number(score).toFixed(2)}/5
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : review.status === "completed" || review.status === "approved" ? (
            <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t("performance.reviews.loadingSummary")}</p>
          ) : (
            <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
              {t("performance.reviews.summaryPending")}
            </p>
          )}

          {/* AI synthesis */}
          {review.submitted_feedbacks > 0 && (
            <div className="pt-2" style={{ borderTop: "1px dashed var(--lm-border)" }}>
              {!aiSummary && !aiLoading && (
                <button onClick={onAiSummarize}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors"
                  style={{ color: "#5e6ad2", border: "1px solid #5e6ad2", background: "rgba(94,106,210,0.06)" }}>
                  <Sparkles className="h-3.5 w-3.5" /> {t("performance.reviews.ai.summarize", { count: review.submitted_feedbacks })}
                </button>
              )}
              {aiLoading && (
                <div className="flex items-center justify-center gap-2 py-3 text-xs" style={{ color: "var(--lm-ink-2)" }}>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("performance.reviews.ai.analyzing")}
                </div>
              )}
              {aiSummary && (
                <AISummaryBlock summary={aiSummary} t={t} onRegenerate={onAiSummarize} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AISummaryBlock({ summary, t, onRegenerate }) {
  const copyParagraph = () => {
    if (!summary.manager_paragraph) return;
    navigator.clipboard.writeText(summary.manager_paragraph);
    toast.success(t("performance.reviews.ai.copied"));
  };

  return (
    <div className="space-y-3 rounded-lg p-3" style={{ backgroundColor: "rgba(94,106,210,0.05)", border: "1px solid rgba(94,106,210,0.20)" }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "#5e6ad2" }}>
          <Sparkles className="h-3.5 w-3.5" /> {t("performance.reviews.ai.synthesis", { count: summary.feedbacks_used })}
        </div>
        <button onClick={onRegenerate} title={t("performance.reviews.ai.regenerate")}
          className="text-xs flex items-center gap-1 px-2 py-0.5 rounded" style={{ color: "var(--lm-ink-2)" }}>
          <RefreshCw className="h-3 w-3" />
        </button>
      </div>

      {summary.executive_summary && (
        <p className="text-xs italic" style={{ color: "var(--lm-ink)" }}>{summary.executive_summary}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {summary.strengths?.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold mb-1" style={{ color: "#16a34a" }}>{t("performance.reviews.ai.strengths")}</p>
            <ul className="text-xs space-y-0.5 list-disc list-inside" style={{ color: "var(--lm-ink)" }}>
              {summary.strengths.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        )}
        {summary.growth_areas?.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold mb-1" style={{ color: "#f59e0b" }}>{t("performance.reviews.ai.growthAreas")}</p>
            <ul className="text-xs space-y-0.5 list-disc list-inside" style={{ color: "var(--lm-ink)" }}>
              {summary.growth_areas.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        )}
      </div>

      {summary.divergences?.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold mb-1" style={{ color: "#dc2626" }}>{t("performance.reviews.ai.divergences")}</p>
          <ul className="text-xs space-y-0.5 list-disc list-inside" style={{ color: "var(--lm-ink)" }}>
            {summary.divergences.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {summary.recommended_actions?.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold mb-1" style={{ color: "#5e6ad2" }}>{t("performance.reviews.ai.recommendedActions")}</p>
          <div className="space-y-1">
            {summary.recommended_actions.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-xs" style={{ color: "var(--lm-ink)" }}>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: "var(--lm-s1)", color: "var(--lm-ink-2)" }}>
                  {a.horizon || "—"}
                </span>
                <span>{a.action}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary.manager_paragraph && (
        <div className="pt-2" style={{ borderTop: "1px solid var(--lm-border)" }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-semibold" style={{ color: "var(--lm-ink-2)" }}>{t("performance.reviews.ai.readyParagraph")}</p>
            <button onClick={copyParagraph} className="text-xs flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/20"
              style={{ color: "#5e6ad2" }}>
              <Copy className="h-3 w-3" /> {t("performance.reviews.ai.copy")}
            </button>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: "var(--lm-ink)" }}>{summary.manager_paragraph}</p>
        </div>
      )}

      <p className="text-[10px] italic" style={{ color: "var(--lm-ink-3)" }}>{summary.disclaimer}</p>
    </div>
  );
}
